import { useCallback, useMemo, useSyncExternalStore } from 'react'
import type { TabStyle } from '@/components/WorkspaceTabs/tabStyle'

const LOCAL_SETTINGS_KEY = 'app_local_settings'

export type LocalSettingKey =
  | 'feedbackToasterDismissed'
  | 'challengeDisclaimerDismissed'
  | 'dismissedGoToAssignments'
  | 'announcedReleaseNoteIds'
  | 'browserTranslationDismissed'
  | 'fontSize'
  | 'tabStyle'
  | 'autosaveIndicator'
  | 'confirmTabClose'

type LocalSettingsValues = {
  feedbackToasterDismissed: boolean
  challengeDisclaimerDismissed: Record<string, boolean>
  dismissedGoToAssignments: string | null
  announcedReleaseNoteIds: string[]
  browserTranslationDismissed: boolean
  fontSize: number
  tabStyle: TabStyle
  autosaveIndicator: boolean
  confirmTabClose: boolean
}

type LocalSettings = Partial<LocalSettingsValues>

const getStoredSettings = (): LocalSettings => {
  try {
    const item = window.localStorage.getItem(LOCAL_SETTINGS_KEY)
    return item ? JSON.parse(item) : {}
  } catch (error) {
    console.warn('Error reading local settings:', error)
    return {}
  }
}

const setStoredSettings = (settings: LocalSettings): void => {
  try {
    window.localStorage.setItem(LOCAL_SETTINGS_KEY, JSON.stringify(settings))
    window.dispatchEvent(new CustomEvent('local-settings-change'))
  } catch (error) {
    console.warn('Error saving local settings:', error)
  }
}

const subscribe = (callback: () => void) => {
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === LOCAL_SETTINGS_KEY) {
      callback()
    }
  }
  const handleCustomChange = () => callback()

  window.addEventListener('storage', handleStorageChange)
  window.addEventListener('local-settings-change', handleCustomChange)

  return () => {
    window.removeEventListener('storage', handleStorageChange)
    window.removeEventListener('local-settings-change', handleCustomChange)
  }
}

const getSnapshot = () => {
  return window.localStorage.getItem(LOCAL_SETTINGS_KEY) ?? '{}'
}

export const useLocalSettings = () => {
  const settingsString = useSyncExternalStore(subscribe, getSnapshot)

  const settings = useMemo<LocalSettings>(() => {
    try {
      return JSON.parse(settingsString)
    } catch {
      return {}
    }
  }, [settingsString])

  const getSetting = useCallback(<K extends LocalSettingKey>(
    key: K
  ): LocalSettingsValues[K] | undefined => {
    return settings[key] as LocalSettingsValues[K] | undefined
  }, [settings])

  const setSetting = useCallback(<K extends LocalSettingKey>(
    key: K,
    value: LocalSettingsValues[K]
  ): void => {
    const currentSettings = getStoredSettings()
    setStoredSettings({
      ...currentSettings,
      [key]: value,
    })
  }, [])

  return { getSetting, setSetting }
}

